import { reject, equals, dissoc, mergeAll, omit, merge } from 'ramda';
import { reducer as formReducer } from 'redux-form';
import dtmReducer from './reducers/dtmReducer';
import crossSellReducer from './reducers/crossSellReducer';
import crossSellOfferReducer from './reducers/crossSellOfferReducer';
import loanApplicationReducer from './reducers/loanApplicationReducer';
import addBankReducer from './reducers/bankAccountReducer';

const defaultHandler = (state, action) => state;

const removeType = (action) => dissoc('type', action);
const removeErrors = (state) => dissoc('errors', state);
const clearState = (state) => omit(['prospect', 'loanAmount', 'email', 'dateOfBirth', 'grossAnnualIncome', 'additionalIncome', 'applicationId', 'quoteId', 'quoteStatus', 'quotes', 'quote', 'errors', 'resetApplicationStep'], state);

const checkYourRate = (state, action) => {
  return mergeAll([
    clearState(state),
    { step: 'landing' }
  ]);
};

const displayErrors = (state, action) =>
  mergeAll([
    state,
    removeType(action)
  ]);

const startLoading = (state, action) =>
  mergeAll([
    removeErrors(state),
    { isLoading: true }
  ]);

const finishLoading = (state, action) =>
  mergeAll([
    state,
    { isLoading: false }
  ]);

const closeError = (state, action) =>
  mergeAll([
    state,
    {
      errors: reject(equals(action.error), state.errors)
    }
  ]);

const applicationDecision = (state, action) => {
  const applicationDecision = action.applicationDecision;
  switch (applicationDecision.pageId) {

  case 'decline':
    return mergeAll([
      clearState(state),
      { prospect: { name: { ...state.prospect.name } } },
      { step: 'decline', isLoading: false },
      { focus: action.meta.focus },
      { dtmData: { 'pageInfo': { 'pageName': 'DECLINED:APPLICATION', 'siteSection': 'APPLY' } } },
      { applicationDecision }
    ]);

  case 'applicationReference':
    return mergeAll([
      state,
      { step: 'applicationPended', isLoading: false },
      { focus: action.meta.focus },
      { dtmData: { 'pageInfo': { 'pageName': 'PENDED:APPLICATION', 'siteSection': 'APPLY' } } },
      { applicationDecision }
    ]);

  case 'verid':
    return mergeAll([
      state,
      { step: 'verid' },
      { applicationDecision }
    ]);

  case 'approved':
    return mergeAll([
      state,
      { step: 'applicationApproved', isLoading: false },
      { focus: action.meta.focus },
      { dtmData: { 'pageInfo': { 'pageName': 'APPROVED:APPLICATION', 'siteSection': 'APPLY' } } },
      { applicationDecision }
    ]);

  default:
    return state;
  }
};

const quoteReceived = (state, action) => {
  if (action.quote.status === 'DECLINED') {
    return mergeAll([
      clearState(state),
      { prospect: { name: { ...state.prospect.name } } },
      { step: 'decline', isLoading: false },
      { dtmData: { 'pageInfo': { 'pageName': 'DECLINED:APPLICATION', 'siteSection': 'APPLY' } } },
      { focus: action.meta.focus }
    ]);
  } else { return state; }
};

const veridQuestionsReceived = (state, action) =>
  mergeAll([
    removeErrors(state),
    { isLoading: false },
    { focus: action.meta.focus },
    { veridData: action.veridData },
    { dtmData: { 'pageInfo': { 'pageName': 'VERID_AUTH', 'siteSection': 'APPLY' } } }
  ]);

const veridQuestionsFetchFailed = (state, action) =>
  mergeAll([
    removeErrors(state),
    { step: 'applicationPended', isLoading: false },
    { focus: 'applicationPended' },
    { dtmData: { 'pageInfo': { 'pageName': 'PENDED:APPLICATION', 'siteSection': 'APPLY' } } }
  ]);

const applicationStatusFetchFailed = (state, action) => {
  let applicationDecision = action.data || state.applicationDecision;
  return mergeAll([
    removeErrors(state),
    { step: 'applicationPended', isLoading: false },
    { focus: 'applicationPended' },
    { dtmData: { 'pageInfo': { 'pageName': 'PENDED:APPLICATION', 'siteSection': 'APPLY' } } },
    { applicationDecision }
  ]);
};

const veridTimerExpired = (state, action) =>
  mergeAll([
    removeErrors(state),
    { step: 'applicationPended' },
    { focus: 'applicationPended' },
    { dtmData: { 'pageInfo': { 'pageName': 'PENDED:APPLICATION', 'siteSection': 'APPLY' } } }
  ]);

const veridError = (state, action) =>
  mergeAll([
    removeErrors(state),
    { step: 'applicationPended', isLoading: false },
    { focus: 'applicationPended' },
    { dtmData: { 'pageInfo': { 'pageName': 'PENDED:APPLICATION', 'siteSection': 'APPLY' } } }
  ]);

const landingFormSubmitted = (state, action) =>
  mergeAll([
    state,
    { step: 'miniApp' }
  ]);

const prospectValidated = (state, action) =>
  mergeAll([
    state,
    {
      step: 'miniApp',
      campaign: action.data.campaign,
      prospect: action.data.prospect
    }
  ]);

const checkYourRateFormSubmitted = (state, action) =>
  mergeAll([
    state,
    {
      prospect: state.prospect || { name: { firstName: action.data.firstName } },
      step: 'quotes'
    }
  ]);

const selectOfferFormSubmitted = (state, action) =>
  mergeAll([
    state,
    { step: 'fullApp' }
  ]);

const loanApplicationReceived = (state, action) =>
  mergeAll([
    state,
    {
      prospect: action.loanApplication.applicantName ? { name: { firstName: action.loanApplication.applicantName.firstName } } : undefined,
      step: 'comboApp'
    }
  ]);

const customerInfoFromTokenReceived = (state, action) =>
  mergeAll([
    state,
    { step: 'miniApp' }
  ]);

const marketCodeChanged = (state, action) =>
  mergeAll([
    clearState(state),
    action.data
  ]);

const handleBackButtonToLandingPage = (state, action) =>
  mergeAll([
    clearState(state),
    { step: 'landing' },
    { resetApplicationStep: true }
  ]);

const markStepAsInterstitial = (state, action) =>
  mergeAll([
    state,
    { step: 'interstitial' },
    { timeout: { timeout: { aboutToTimeoutValue: 300000, sessionTimeoutValue: 900000 } } }
  ]);

const fireDtmController = (state, action) => state;

const toggleNoInvitationDialog = (state, action) =>
  mergeAll([
    state,
    { isNoInvitationDialogOpen: !state.isNoInvitationDialogOpen }
  ]);

const updateCampaignData = (state, action) => {
  let envConfig = state.envConfig;
  envConfig.campaign = action.data.campaign;
  return mergeAll([
    state,
    { envConfig: envConfig }
  ]);
};

const mainReducer = (state, action) => ({
  displayErrors,
  checkYourRate,
  startLoading,
  finishLoading,
  closeError,
  applicationDecision,
  quoteReceived,
  veridQuestionsReceived,
  veridQuestionsFetchFailed,
  veridTimerExpired,
  veridError,
  applicationStatusFetchFailed,
  landingFormSubmitted,
  prospectValidated,
  checkYourRateFormSubmitted,
  selectOfferFormSubmitted,
  marketCodeChanged,
  loanApplicationReceived,
  customerInfoFromTokenReceived,
  handleBackButtonToLandingPage,
  markStepAsInterstitial,
  fireDtmController,
  toggleNoInvitationDialog,
  updateCampaignData
}[action.type] || defaultHandler)(state, action);

export default (state, action) =>
  mergeAll([
    omit(['form', 'dtmData', 'loanApplication'], mainReducer(state, action)),
    {
      form: formReducer(state.form, action),
      dtm: dtmReducer(merge(state.dtm, { marketCode: state.marketCode }), action),
      loanApplication: state.resetApplicationStep ? {} : loanApplicationReducer(state.loanApplication, action),
      creditCardApplicant: crossSellReducer(state.creditCardApplicant, action),
      offerDetails: state.creditCardApplicant?crossSellOfferReducer(state.creditCardApplicant.offerDetails, action):crossSellOfferReducer(state.creditCardApplicant, action),
      bankAccountInfo: addBankReducer(state.bankAccountInfo, action)
    }
  ]);
